import { useEffect } from 'react';
import { playSignatureTone, stopSignatureTone } from '../utils/codexAlarms';
import { syncNativeAlarms, nativeAlarmsAvailable, dismissDeliveredAlarms, onAlarmNotificationTap } from '../utils/nativeAlarms';
import { vibrate } from '../utils/haptics';

/* ═══════════════════════════════════════════════════════════════════
   CodexAlarmWatcher — a headless component mounted at the App root
   that continuously scans the Aura Codex state in localStorage and
   fires alarms even when the Codex modal is closed.

   Same behaviour as the in-modal tick:
     • Every 1 second reads `aura_codex_state_v1` from localStorage.
     • If ANY pillar's `alarms[]` list contains an HH:MM matching the
       current minute AND the pillar is not yet fused today → open a
       5-minute Fuse Window.
     • At t=0, 1, 2, 3, 4 minutes into the window fire the 15-second
       signature tone (Web Audio); no in-app banner/notification.
     • Cancels immediately if the pillar becomes `fused` OR the 5-min
       window expires.
     • Broadcasts `codex-alarm` CustomEvents so tiles/orbs can blink.
   ═══════════════════════════════════════════════════════════════════ */

const STORAGE_KEY = 'aura_codex_state_v1';

const readState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { pillars: {} };
    return JSON.parse(raw) || { pillars: {} };
  } catch (_) { return { pillars: {} }; }
};

const startAlarm = (pillarId) => {
  try { localStorage.setItem('codex_alarm_active', pillarId); } catch (_) { /* ignore */ }
  try { localStorage.setItem('codex_alarm_started_at', String(Date.now())); } catch (_) { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent('codex-alarm', {
      detail: { pillarId, phase: 'start' },
    }));
  } catch (_) { /* ignore */ }
};

const stopAlarm = () => {
  try { stopSignatureTone(); } catch (_) { /* ignore */ }
  try { localStorage.removeItem('codex_alarm_active'); } catch (_) { /* ignore */ }
  try { localStorage.removeItem('codex_alarm_started_at'); } catch (_) { /* ignore */ }
  try {
    window.dispatchEvent(new CustomEvent('codex-alarm', {
      detail: { pillarId: null, phase: 'stop' },
    }));
  } catch (_) { /* ignore */ }
};

export default function CodexAlarmWatcher() {
  useEffect(() => {
    let lastPlanKey = null;
    let planTimer = null;
    const planNative = () => {
      if (!nativeAlarmsAvailable) return;
      let raw = '';
      try { raw = localStorage.getItem(STORAGE_KEY) || ''; } catch (_) { raw = ''; }
      const key = `${new Date().toDateString()}|${raw}`;
      if (key === lastPlanKey) return;
      lastPlanKey = key;
      clearTimeout(planTimer);
      planTimer = setTimeout(() => syncNativeAlarms(readState()), 1500);
    };
    const tick = () => {
      planNative();
      const now = new Date();
      const currentKey =
        `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}-${now.getHours()}-${now.getMinutes()}`;

      // Check if an alarm is currently active
      let activeId = null;
      let started = 0;
      try { activeId = localStorage.getItem('codex_alarm_active'); } catch (_) { activeId = null; }
      try { started = parseInt(localStorage.getItem('codex_alarm_started_at') || '0', 10); }
      catch (_) { started = 0; }

      if (activeId && started) {
        const elapsedMs = Date.now() - started;
        // Stop after 1 minute — alarm is only an initiation reminder, not a
        // fusing window. Fusing remains available all 24 h in the tile.
        if (elapsedMs >= 60 * 1000) { stopAlarm(); return; }
        return;
      }

      // No alarm active — scan for a match
      const st = readState();
      const pillars = st.pillars || {};
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      for (const pillarId of Object.keys(pillars)) {
        const ps = pillars[pillarId];
        // A fuse only silences the alarm on the SAME day — yesterday's fuse must not mute today.
        if (!ps || (ps.fused && st.date === today)) continue;
        const alarms = Array.isArray(ps.alarms)
          ? ps.alarms
          : (ps.window?.start ? [ps.window.start] : []);
        for (const hhmm of alarms) {
          if (!hhmm || typeof hhmm !== 'string') continue;
          const [hStr, mStr] = hhmm.split(':');
          const hh = parseInt(hStr, 10);
          const mm = parseInt(mStr, 10);
          if (isNaN(hh) || isNaN(mm)) continue;
          if (hh === now.getHours() && mm === now.getMinutes()) {
            const guardKey = `codex_alarm_last_fired_${pillarId}_${hhmm}_${currentKey}`;
            try {
              if (localStorage.getItem(guardKey)) continue;
              localStorage.setItem(guardKey, '1');
            } catch (_) { /* ignore */ }
            startAlarm(pillarId);
            playSignatureTone();
            vibrate([400, 200, 400, 200, 800]);
            setTimeout(dismissDeliveredAlarms, 1500);
            setTimeout(dismissDeliveredAlarms, 4000);
            return;
          }
        }
      }
    };
    tick();
    const iv = window.setInterval(tick, 1000);
    // Opened from an alarm notification → blink that pillar's tile in-app.
    const offTap = onAlarmNotificationTap((pid) => startAlarm(pid));
    return () => { clearInterval(iv); clearTimeout(planTimer); offTap(); };
  }, []);

  return null;
}
